import { useState, useEffect } from "react";
import { Store, Plus, ExternalLink, ShoppingBag, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { RecommendStoreDialog } from "./RecommendStoreDialog";
import { GamificationBadges } from "./GamificationBadges";
import { format, addYears, isBefore } from "date-fns";
import { cn } from "@/lib/utils";

interface GiftParticipation {
  id: string;
  request_id: string;
  name: string;
  email: string;
  has_paid: boolean;
  is_organizer: boolean;
  request: {
    id: string;
    product_name: string;
    product_image_url: string | null;
    product_description: string | null;
    status: string;
    deadline: string;
    total_price: number;
    split_amount: number;
    customer_name: string;
    is_gift: boolean;
  };
}

interface EventInfo {
  id: string;
  date: Date;
  eventName: string;
  recipientName: string | null;
  type?: 'gift' | 'purchase' | 'payment';
}

interface CustomerEngagementSectionProps {
  customerEmail?: string | null;
  customerPhone?: string | null;
  gifts?: GiftParticipation[];
}

export function CustomerEngagementSection({ customerEmail, customerPhone, gifts = [] }: CustomerEngagementSectionProps) {
  const [eventDates, setEventDates] = useState<Date[]>([]);
  const [eventInfoMap, setEventInfoMap] = useState<Map<string, EventInfo>>(new Map());
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  useEffect(() => {
    fetchEventDates();
  }, [customerEmail, customerPhone]);

  const fetchEventDates = async () => {
    if (!customerEmail && !customerPhone) return;

    try {
      const today = new Date();
      const dates: Date[] = [];
      const infoMap = new Map<string, EventInfo>();
      
      if (customerEmail) {
        const emailLower = customerEmail.toLowerCase();
        
        // Fetch reminders user created
        const { data: createdData } = await supabase
          .from("gift_reminders")
          .select("id, event_date, is_recurring, event_name, recipient_name")
          .eq("customer_email", emailLower);

        // Fetch reminders where user is invited (reminder_email or invited_friend_email)
        const { data: invitedData1 } = await supabase
          .from("gift_reminders")
          .select("id, event_date, is_recurring, event_name, recipient_name")
          .eq("reminder_email", emailLower)
          .or(`invited_friend_status.is.null,invited_friend_status.eq.active`);

        const { data: invitedData2 } = await supabase
          .from("gift_reminders")
          .select("id, event_date, is_recurring, event_name, recipient_name")
          .eq("invited_friend_email", emailLower)
          .or(`invited_friend_status.is.null,invited_friend_status.eq.active`);

        // Fetch group purchases where user is organizer or active participant
        const { data: orgPurchases } = await supabase
          .from("group_purchases")
          .select("id, target_date, title, payment_type, category")
          .eq("organizer_email", emailLower)
          .eq("status", "active")
          .not("target_date", "is", null);

        const { data: participantData } = await supabase
          .from("group_purchase_participants")
          .select("purchase_id")
          .eq("email", emailLower)
          .eq("status", "active");

        let partPurchases: any[] = [];
        if (participantData && participantData.length > 0) {
          const purchaseIds = participantData.map(p => p.purchase_id);
          const { data } = await supabase
            .from("group_purchases")
            .select("id, target_date, title, payment_type, category")
            .in("id", purchaseIds)
            .eq("status", "active")
            .not("target_date", "is", null);
          partPurchases = data || [];
        }

        // Combine and deduplicate all data
        const allReminders = [
          ...(createdData || []),
          ...(invitedData1 || []),
          ...(invitedData2 || [])
        ];
        const uniqueReminders = allReminders.filter((r, index, self) => 
          index === self.findIndex(t => t.id === r.id)
        );
        
        const allPurchases = [...(orgPurchases || []), ...partPurchases];
        const uniquePurchases = allPurchases.filter((p, index, self) => 
          index === self.findIndex(t => t.id === p.id)
        );

        // Process reminders
        uniqueReminders.forEach(reminder => {
          const eventDate = new Date(reminder.event_date);
          let nextDate: Date;
          
          if (reminder.is_recurring) {
            nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
            if (isBefore(nextDate, today)) {
              nextDate = addYears(nextDate, 1);
            }
          } else {
            nextDate = eventDate;
          }
          
          dates.push(nextDate);
          const dateKey = format(nextDate, "yyyy-MM-dd");
          infoMap.set(dateKey, {
            id: reminder.id,
            date: nextDate,
            eventName: reminder.event_name,
            recipientName: reminder.recipient_name,
            type: 'gift'
          });
        });

        // Process purchases and payments
        uniquePurchases.forEach(purchase => {
          if (purchase.target_date) {
            const purchaseDate = new Date(purchase.target_date);
            dates.push(purchaseDate);
            const dateKey = format(purchaseDate, "yyyy-MM-dd");
            if (!infoMap.has(dateKey)) {
              // Use explicit category field set during creation
              const isPayment = (purchase as any).category === 'payment';
              infoMap.set(dateKey, {
                id: purchase.id,
                date: purchaseDate,
                eventName: purchase.title,
                recipientName: null,
                type: isPayment ? 'payment' : 'purchase'
              });
            }
          }
        });
      } else if (customerPhone) {
        const { data } = await supabase
          .from("gift_reminders")
          .select("id, event_date, is_recurring, event_name, recipient_name")
          .eq("customer_phone", customerPhone);
        
        if (data) {
          data.forEach(reminder => {
            const eventDate = new Date(reminder.event_date);
            let nextDate: Date;
            
            if (reminder.is_recurring) {
              nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
              if (isBefore(nextDate, today)) {
                nextDate = addYears(nextDate, 1);
              }
            } else {
              nextDate = eventDate;
            }
            
            dates.push(nextDate);
            const dateKey = format(nextDate, "yyyy-MM-dd");
            infoMap.set(dateKey, {
              id: reminder.id,
              date: nextDate,
              eventName: reminder.event_name,
              recipientName: reminder.recipient_name,
              type: 'gift'
            });
          });
        }
      }
      
      setEventDates(dates);
      setEventInfoMap(infoMap);
    } catch (error) {
      console.error("Error fetching event dates:", error);
    }
  };

  const getEventForDate = (date: Date): EventInfo | undefined => {
    const dateKey = format(date, "yyyy-MM-dd");
    return eventInfoMap.get(dateKey);
  };

  return (
    <section className="mt-6">
      {/* Top Row: Calendar, Stores, Journey - all in one line */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Left: Mini Calendar with Events */}
        <Card className="border-border/50">
          <CardHeader className="py-3 px-4 pb-2">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-customer" />
              <CardTitle className="text-sm font-medium">Your Events</CardTitle>
            </div>
            <CardDescription className="text-xs mt-1">
              {eventDates.length > 0 
                ? `${eventDates.length} upcoming`
                : "Click to add"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="py-2 px-3 pt-0 overflow-visible">
            <TooltipProvider delayDuration={100}>
              <Calendar
                mode="multiple"
                selected={eventDates}
                fixedWeeks
                className="rounded-md border-0 p-0 w-full pointer-events-auto [&_.rdp-caption]:text-xs [&_.rdp-cell]:p-0.5 [&_.rdp-button]:h-6 [&_.rdp-button]:w-6 [&_.rdp-button]:text-[10px] [&_.rdp-head_cell]:text-[9px] [&_.rdp-head_cell]:w-6 [&_.rdp-button:not(.rdp-day_selected)]:hover:bg-customer/20 [&_.rdp-button:not(.rdp-day_selected)]:cursor-pointer"
                modifiers={{
                  event: eventDates
                }}
                components={{
                  DayContent: ({ date }) => {
                    const eventInfo = getEventForDate(date);
                    if (eventInfo) {
                      // Determine colors based on event type
                      const colorClass = eventInfo.type === 'payment' 
                        ? 'text-payment' 
                        : eventInfo.type === 'purchase' 
                          ? 'text-purchase' 
                          : 'text-gift';
                      const bgClass = eventInfo.type === 'payment'
                        ? 'bg-payment text-payment-foreground'
                        : eventInfo.type === 'purchase'
                          ? 'bg-purchase text-purchase-foreground'
                          : 'bg-gift text-gift-foreground';
                      
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span 
                              className={cn(
                                "w-full h-full flex items-center justify-center cursor-pointer rounded-full font-semibold",
                                bgClass
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Navigate to the correct page based on event type
                                if (eventInfo.type === 'purchase' || eventInfo.type === 'payment') {
                                  window.location.href = `/purchases/${eventInfo.id}`;
                                } else {
                                  window.location.href = `/events/${eventInfo.id}`;
                                }
                              }}
                            >
                              {date.getDate()}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top" 
                            sideOffset={8}
                            className="bg-popover border border-border shadow-lg p-2 z-[100]"
                          >
                            <div className="text-xs">
                              <p className={cn("font-medium", colorClass)}>{eventInfo.eventName}</p>
                              {eventInfo.recipientName && (
                                <p className="text-muted-foreground">For: {eventInfo.recipientName}</p>
                              )}
                              <p className={cn("mt-1 opacity-70", colorClass)}>Click to view →</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }
                    return <span>{date.getDate()}</span>;
                  }
                }}
                onDayClick={(day) => {
                  const eventInfo = getEventForDate(day);
                  if (eventInfo) {
                    // Navigate to the correct discussion page based on event type
                    if (eventInfo.type === 'purchase' || eventInfo.type === 'payment') {
                      window.location.href = `/purchases/${eventInfo.id}`;
                    } else {
                      window.location.href = `/events/${eventInfo.id}`;
                    }
                  } else {
                    // Open add event dialog for non-event days
                    setSelectedDate(day);
                    setAddEventDialogOpen(true);
                  }
                }}
              />
            </TooltipProvider>
          </CardContent>
        </Card>

        {/* Middle: Stores */}
        <Card className="border-border/50">
          <CardHeader className="py-3 px-4 pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 text-customer" />
                <CardTitle className="text-sm font-medium">Your Stores</CardTitle>
              </div>
              <RecommendStoreDialog>
                <Button size="sm" variant="ghost" className="text-customer hover:bg-customer/10 h-6 px-2 text-xs">
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
              </RecommendStoreDialog>
            </div>
            <CardDescription className="text-xs mt-1">
              Stores with group purchase
            </CardDescription>
          </CardHeader>
          <CardContent className="py-3 px-4 pt-0 flex-1 flex flex-col justify-between">
            <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed border-border/50 rounded-md mb-3">
              Stores you purchase from will appear here
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-customer border-customer/30 hover:bg-customer/10 text-xs"
              onClick={() => window.open('/gallery', '_blank')}
            >
              <ShoppingBag className="w-3 h-3 mr-1" />
              Browse Stores
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Right: Journey (Badges) */}
        <GamificationBadges gifts={gifts} title="Your Journey" />
      </div>
    </section>
  );
}
